import { MenuItem, Paper, Tab, Tabs, TextField, Theme } from '@mui/material';
import { Box, Stack, styled } from '@mui/system';
import PageHeader from '../../../controls/PageHeader';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { ReactNode, useEffect, useState } from 'react';
import omit from 'lodash/omit';
import upperFirst from 'lodash/upperFirst';
import TabPanel from '../../../TabPanel';
import { InventoryManagementProperties } from './InventoryManagementProperties';
import { InventoryManagementActivity } from './InventoryManagementActivity';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import * as tripLegSlice from '../../../../data/tripleg.slice';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { InventoryServiceConfiguration } from '../../../../data/models/inventory-service-configuration.model';
import { useIsSuperReaderUser } from '../../../../hooks/permissions/useIsSuperReaderUser';
import ConfirmationDialog from '../../../ConfirmationDialog';

const PREFIX = 'InventoryManagement';

const classes = {
  container: `${PREFIX}-container`,
  headerContainer: `${PREFIX}-headerContainer`,
  subHeader: `${PREFIX}-header`,
  contentContainer: `${PREFIX}-contentContainer`,
  search: `${PREFIX}-search`,
  statusSelectContainer: `${PREFIX}-statusSelectContainer`,
  statusSelect: `${PREFIX}-statusSelect`,
  addButton: `${PREFIX}-addButton`,
  tabsContainer: `${PREFIX}-tabsContainer`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(1),
  },
  [`& .${classes.headerContainer}`]: {
    '& > *': {
      flex: 1,
    },
  },
  [`& .${classes.contentContainer}`]: {
    padding: theme.spacing(2),
  },
  [`& .${classes.subHeader}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.search}`]: {
    width: 320,
    marginRight: theme.spacing(3),
  },
  [`& .${classes.statusSelectContainer}`]: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'end',
    justifyContent: 'end',
  },
  [`& .${classes.statusSelect}`]: {
    width: 165,
  },
  [`& .${classes.addButton}`]: {
    width: 150,
    fontSize: 15,
  },
  [`& .${classes.tabsContainer}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

enum ScheduledPackagingStatus {
  Active = 'active',
  Inactive = 'inactive',
}

export function InventoryManagement() {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const isSuperReaderUser = useIsSuperReaderUser();
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(
    ScheduledPackagingStatus.Inactive,
  );
  const [confirmStatusDialogOpen, setConfirmStatusDialogOpen] = useState(false);
  const [confirmStatusMessage, setConfirmStatusMessage] =
    useState<ReactNode>('');
  const serviceConfiguration = useAppSelector(
    tripLegSlice.selectServiceConfiguration,
  );
  const savingServiceConfiguration = useAppSelector(
    tripLegSlice.selectSavingServiceConfiguration,
  );

  useEffect(() => {
    dispatch(tripLegSlice.fetchServiceConfigurationAsync());
  }, []);

  useEffect(() => {
    if (serviceConfiguration) {
      setSelectedStatus(
        serviceConfiguration.scheduledPackagingEnabled
          ? ScheduledPackagingStatus.Active
          : ScheduledPackagingStatus.Inactive,
      );
    }
  }, [serviceConfiguration]);

  const scheduledPackagingStatuses = Object.values(ScheduledPackagingStatus);

  const saveScheduledPackaging = (
    enabled: boolean,
    serviceConfiguration: InventoryServiceConfiguration,
  ) => {
    const updatedServiceConfig = {
      ...omit(serviceConfiguration, [
        'correlationUUID',
        'modifiedByUserID',
        'modifiedDateTimeUTC',
      ]),
      scheduledPackagingEnabled: enabled,
    } as InventoryServiceConfiguration;
    dispatch(tripLegSlice.saveServiceConfigurationAsync(updatedServiceConfig));
  };

  const handleStatusChange = (status: ScheduledPackagingStatus) => {
    if (!serviceConfiguration) {
      return;
    }
    setConfirmStatusDialogOpen(true);
    const statusWord =
      status === ScheduledPackagingStatus.Active
        ? t('inventoryManagement.active')
        : t('inventoryManagement.inactive');
    // Word "active"/"inactive" should be in bold
    const statusElement = <b>{statusWord}</b>;
    let confirmStatusMessage =
      status === ScheduledPackagingStatus.Active
        ? t('inventoryManagement.confirmStatusActiveUpdateMessage', {
            status: statusWord,
          })
        : t('inventoryManagement.confirmStatusInactiveUpdateMessage', {
            status: statusWord,
          });
    confirmStatusMessage = `${confirmStatusMessage}`;
    const splitMessage = confirmStatusMessage.split(statusWord);

    const confirmStatusMessageElement = (
      <>
        {splitMessage[0]} {statusElement}
        {splitMessage[1]}
        <br />
        <br />
        {t('inventoryManagement.confirmStatusQuestion')}
      </>
    );
    setConfirmStatusMessage(confirmStatusMessageElement);
  };

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleConfirmStatusPositiveAction = () => {
    if (!serviceConfiguration) {
      return;
    }
    setConfirmStatusDialogOpen(false);
    const newStatus =
      selectedStatus === ScheduledPackagingStatus.Active
        ? ScheduledPackagingStatus.Inactive
        : ScheduledPackagingStatus.Active;
    setSelectedStatus(newStatus);
    const scheduledPackagingEnabled =
      newStatus === ScheduledPackagingStatus.Active;
    saveScheduledPackaging(scheduledPackagingEnabled, serviceConfiguration);
  };

  const handleConfirmStatusNegativeAction = () => {
    setConfirmStatusDialogOpen(false);
  };

  const dialogStyle = {
    '& .MuiDialogTitle-root': (theme: Theme) => ({
      ...theme.typography.h3,
    }),
  };

  const handleViewActivity = () => {
    setTabIndex(1);
  };

  return (
    <StyledPaper>
      <Box className={classes.container}>
        <Stack direction="row" className={classes.headerContainer}>
          <PageHeader
            title={t('inventoryManagement.title')}
            returnLink="/settings"
            returnLinkText={t('settings.title')}
          />
          <Box className={classes.statusSelectContainer}>
            <TextField
              select
              className={classes.statusSelect}
              label={t('inventoryManagement.status')}
              variant="outlined"
              value={selectedStatus}
              disabled={savingServiceConfiguration}
              InputProps={{
                readOnly: isSuperReaderUser,
              }}
              onChange={(evt) =>
                handleStatusChange(evt.target.value as ScheduledPackagingStatus)
              }>
              {scheduledPackagingStatuses?.map((status) => {
                return (
                  <MenuItem value={status} key={status}>
                    {upperFirst(status)}
                  </MenuItem>
                );
              })}
            </TextField>
          </Box>
        </Stack>
        <Box className={classes.tabsContainer}>
          <Tabs value={tabIndex} onChange={handleTabChange} variant="standard">
            <Tab label={t('inventoryManagement.properties')} />
            <Tab label={t('inventoryManagement.activity')} />
          </Tabs>
          <TabPanel value={tabIndex} index={0}>
            <InventoryManagementProperties
              serviceConfiguration={serviceConfiguration ?? undefined}
              handleViewActivity={handleViewActivity}
            />
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <InventoryManagementActivity />
          </TabPanel>
        </Box>
      </Box>
      <ConfirmationDialog
        sx={dialogStyle}
        title={t('inventoryManagement.confirmStatusUpdateTitle')}
        message={confirmStatusMessage}
        open={confirmStatusDialogOpen}
        positiveText={t('inventoryManagement.confirm')}
        negativeText={t('inventoryManagement.cancel')}
        positiveAction={handleConfirmStatusPositiveAction}
        negativeAction={handleConfirmStatusNegativeAction}
      />
    </StyledPaper>
  );
}
